<template>
  <div class="ledger-create-container">
    <!-- 页面标题 -->
    <div class="page-header">
      <div class="header-title">
        <div class="title-mark"></div>
        <span>{{ isEdit ? '编辑台账' : '新增台账' }}</span>
      </div>
      <div class="header-actions">
        <el-button @click="goBack" icon="el-icon-arrow-left">返回</el-button>
      </div>
    </div>
    
    <!-- 表单内容 -->
    <div class="form-content">
      <el-card shadow="never">
        <div slot="header" class="card-header">
          <span>台账基本信息</span>
        </div>
        <el-form :model="form" :rules="rules" ref="form" label-width="150px">
          <el-form-item label="关联合同" prop="contract_id">
            <el-select
              v-model="form.contract_id"
              placeholder="请选择合同"
              filterable
              style="width: 100%"
              @change="handleContractChange"
            >
              <el-option
                v-for="contract in availableContracts"
                :key="contract.id"
                :label="`${contract.contractName || contract.contract_name} (${contract.contractNumber || contract.contract_number || contract.id})`"
                :value="contract.id"
              >
                <div>
                  <div style="font-weight: bold; margin-bottom: 4px; color: #303133;">
                    {{ contract.contractName || contract.contract_name }} 
                    <span style="color: #909399; font-weight: normal; margin-left: 8px;">
                      ({{ contract.contractNumber || contract.contract_number || contract.id }})
                    </span>
                  </div>
                  <div style="font-size: 12px; color: #606266;">
                    <div style="margin-bottom: 4px;">
                      <span style="color: #909399;">供应商:</span> 
                      <span style="color: #409EFF; font-weight: 500;">{{ contract.supplier_name || '-' }}</span>
                    </div>
                    <div>
                      <span style="color: #909399;">需求方:</span> 
                      <span style="color: #67C23A; font-weight: 500;">{{ contract.customer_name || '-' }}</span>
                    </div>
                  </div>
                </div>
              </el-option>
            </el-select>
            <div class="form-tip">提示：一个合同对应一个台账，收付款记录将在台账详情页中逐笔添加</div>
          </el-form-item>

          <el-form-item label="应付日期时间依据" prop="due_date_basis">
            <el-select
              v-model="form.due_date_basis"
              placeholder="请选择应付日期时间依据"
              style="width: 100%"
              :disabled="isEdit && !dueDateBasisEditable"
            >
              <el-option
                v-for="opt in dueDateBasisOptions"
                :key="opt.value"
                :label="opt.label"
                :value="opt.value"
              />
            </el-select>
            <div class="form-tip">用于计算应付日期：利息计算等场景将根据此字段确定应付日期</div>
            <div v-if="isEdit && !dueDateBasisEditable" class="form-tip" style="color: #E6A23C;">
              该合同已有已确认的利息计算单，应付日期时间依据不可修改。
            </div>
          </el-form-item>
          
          <el-form-item label="备注" prop="remark">
            <el-input
              v-model="form.remark"
              type="textarea"
              :rows="4"
              placeholder="请输入备注信息"
              maxlength="500"
              show-word-limit
            ></el-input>
            <div class="form-tip">可选，用于记录台账的备注信息</div>
          </el-form-item>
        </el-form>
      </el-card>
    </div>
    
    <!-- 操作按钮 -->
    <div class="form-actions">
      <el-button @click="goBack">取消</el-button>
      <el-button 
        type="primary" 
        v-permission="'finance:finance-edit:create'"
        @click="saveLedger"
        :loading="saving"
      >保存台账</el-button>
    </div>
  </div>
</template>

<script>
import { create_fund_ledger, update_fund_ledger, query_contracts_by_unit, query_fund_ledger_by_id } from '@/api/modules/fund-ledger'
import { getUserInfo } from '@/utils/auth'

export default {
  name: 'FinanceEdit',
  data() {
    return {
      isEdit: false,
      ledgerId: null,
      saving: false,
      currentUnitId: null,
      /** 编辑台账时是否允许改应付日依据（存在已确认利息单时为 false） */
      dueDateBasisEditable: true,
      
      form: {
        contract_id: null,
        due_date_basis: 'SETTLEMENT_TIME',
        remark: ''
      },
      rules: {
        contract_id: [
          { required: true, message: '请选择合同', trigger: 'change' }
        ],
        due_date_basis: [
          { required: true, message: '请选择应付日期时间依据', trigger: 'change' }
        ]
      },
      dueDateBasisOptions: [
        { value: 'SETTLEMENT_TIME', label: '结算时间' },
        { value: 'SHIPMENT_TIME', label: '发货时间' },
        { value: 'RECEIPT_TIME', label: '收货时间' }
      ],
      
      availableContracts: [],
      selectedContract: null
    }
  },
  created() {
    const id = this.$route.params.id
    if (id) {
      this.isEdit = true
      this.ledgerId = id
    }
    
    this.getCurrentUnitId()
    
    if (this.isEdit && this.ledgerId) {
      this.loadLedgerDetail()
    }
  },
  methods: {
    // 获取当前用户单位ID
    getCurrentUnitId() {
      try {
        const userInfo = getUserInfo()
        if (userInfo) {
          this.currentUnitId = userInfo.unitId || userInfo.unit_id
if (this.currentUnitId) {
            this.initAvailableContracts()
          }
        } else {
          console.warn('未能获取用户信息')
        }
      } catch (err) {
        console.error('获取用户单位ID出错:', err)
      }
    },
    
    // 初始化可选的合同列表
    async initAvailableContracts() {
      if (!this.currentUnitId) {
        this.$message.warning('无法获取当前用户单位ID，无法加载合同列表')
        return
      }
      
      try {
        const response = await query_contracts_by_unit(this.currentUnitId)
        
        if (response.data && response.data.success) {
          const contractsData = response.data.data || []
          
          this.availableContracts = contractsData.map(contract => {
            let supplier_name = '-'
            let customer_name = '-'
            
            if (contract.settlements && contract.settlements.length > 0) {
              const firstSettlement = contract.settlements[0]
              if (firstSettlement.supplier && firstSettlement.supplier.unitName) {
                supplier_name = firstSettlement.supplier.unitName
              }
              customer_name = this.getDemandUnitName(firstSettlement) || '-'
            }
            
            return {
              id: contract.id,
              contractName: contract.contractName || contract.contract_name,
              contract_name: contract.contractName || contract.contract_name,
              contractNumber: contract.contractNumber || contract.contract_number,
              contract_number: contract.contractNumber || contract.contract_number,
              supplier_name: supplier_name,
              customer_name: customer_name,
              settlements: contract.settlements || []
            }
          })
          
} else {
          this.$message.error('加载合同列表失败: ' + ((response.data && response.data.message) || '未知错误'))
          this.availableContracts = []
        }
      } catch (error) {
        console.error('加载合同列表失败:', error)
        const errorMessage = (error.response && error.response.data && error.response.data.message) || error.message || '未知错误'
        this.$message.error('加载合同列表失败: ' + errorMessage)
        this.availableContracts = []
      }
    },
    
    // 获取需求方名称
    getDemandUnitName(settlement) {
      if (!settlement || !settlement.demandUnits || settlement.demandUnits.length === 0) {
        return ''
      }
      
      const supplierName = settlement.supplier && settlement.supplier.unitName ? settlement.supplier.unitName : ''
      
      if (settlement.demandUnits.length === 1) {
        return settlement.demandUnits[0].name
      }
      
      const firstDemandUnit = settlement.demandUnits[0]
      if (firstDemandUnit.name === supplierName && settlement.demandUnits.length > 1) {
        return settlement.demandUnits[1].name
      }
      
      return firstDemandUnit.name
    },
    
    // 合同选择变更
    handleContractChange(contractId) {
      if (!contractId) {
        this.selectedContract = null
        return
      }
      
      this.selectedContract = this.availableContracts.find(c => c.id === contractId)
      
      if (!this.selectedContract) {
        this.$message.error('未找到选中的合同信息')
        return
      }
    },
    
    // 加载台账详情（编辑模式）
    async loadLedgerDetail() {
      if (!this.ledgerId) {
        return
      }
      
      try {
        this.saving = true
        
        // 等待合同列表加载完成
        let retryCount = 0
        while (this.availableContracts.length === 0 && retryCount < 10) {
          await new Promise(resolve => setTimeout(resolve, 200))
          retryCount++
        }
        
        const response = await query_fund_ledger_by_id(this.ledgerId)
        
        if (response.data && response.data.success) {
          const ledger = response.data.data
          
          this.form.contract_id = ledger.contractId
          this.form.due_date_basis = ledger.dueDateBasis || 'SETTLEMENT_TIME'
          this.form.remark = ledger.remark || ''
          this.dueDateBasisEditable = ledger.dueDateBasisEditable !== false
          
          // 设置选中的合同
          if (ledger.contractId) {
            this.handleContractChange(ledger.contractId)
          }
        } else {
          this.$message.error('获取台账详情失败: ' + ((response.data && response.data.message) || '未知错误'))
        }
      } catch (error) {
        console.error('加载台账详情失败:', error)
        const errorMessage = (error.response && error.response.data && error.response.data.message) || error.message || '未知错误'
        this.$message.error('加载台账详情失败: ' + errorMessage)
      } finally {
        this.saving = false
      }
    },
    
    // 保存台账
    async saveLedger() {
      this.$refs.form.validate(async (valid) => {
        if (!valid) {
          this.$message.error('请完善表单信息')
          return
        }
        
        try {
          await this.$confirm('确认保存台账？', '提示', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          })
        } catch {
          return
        }
        
        this.saving = true
        
        try {
          const requestData = {
            contractId: this.form.contract_id,
            remark: this.form.remark || ''
          }
          if (this.form.due_date_basis) {
            requestData.dueDateBasis = this.form.due_date_basis
          }
          
          let response
          if (this.isEdit && this.ledgerId) {
            response = await update_fund_ledger(this.ledgerId, requestData)
            
            if (response.data && response.data.success) {
              this.$message.success('台账更新成功！')
              // 跳转到详情页
              this.$router.push({ path: `/finance/finance-detail/${this.ledgerId}` })
            } else {
              throw new Error((response.data && response.data.message) || '更新失败')
            }
          } else {
            response = await create_fund_ledger(requestData)
            
            if (response.data && response.data.success) {
              this.$message.success('台账创建成功！请在下方向添加收付款记录')
              // 跳转到详情页，在详情页可以添加收付款记录
              const newLedgerId = response.data.data.id
              this.$router.push({ path: `/finance/finance-detail/${newLedgerId}` })
            } else {
              throw new Error((response.data && response.data.message) || '创建失败')
            }
          }
        } catch (error) {
          console.error('保存台账失败:', error)
          const errorMessage = (error.response && error.response.data && error.response.data.message) || error.message || '未知错误'
          this.$message.error('保存台账失败: ' + errorMessage)
        } finally {
          this.saving = false
        }
      })
    },
    
    // 返回
    goBack() {
      this.$router.push({ path: '/finance/finance-list' })
    }
  }
}
</script>

<style lang="scss" scoped>
.ledger-create-container {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: calc(100vh - 84px);
  
  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding: 15px 20px;
    background: #fff;
    border-radius: 4px;
    
    .header-title {
      display: flex;
      align-items: center;
      font-size: 18px;
      font-weight: bold;
      
      .title-mark {
        width: 4px;
        height: 18px;
        background: #409EFF;
        margin-right: 10px;
      }
    }
  }
  
  .form-content {
    background: #fff;
    padding: 30px;
    border-radius: 4px;
    margin-bottom: 20px;
    
    .card-header {
      font-size: 16px;
      font-weight: bold;
      color: #303133;
    }
    
    .form-tip {
      margin-top: 5px;
      font-size: 12px;
      color: #909399;
    }
  }
  
  .form-actions {
    text-align: center;
    padding: 20px;
    background: #fff;
    border-radius: 4px;
    
    .el-button {
      margin: 0 10px;
    }
  }
}

::v-deep .el-form-item__label {
  font-weight: 500;
}
</style>
